import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { useTable, useSortBy, useRowSelect, useGlobalFilter } from 'react-table'
import { NoticeMessage } from '../../../../../components'

import { RenderIf } from '../../../../../utils/fns'

import '../../../../../globals/scss/components/tableview-virtualized.scss'

const ResultTable = (props) => {
  const {
    columns,
    data,
    getHeaderProps,
    getColumnProps,
    getRowProps,
    getCellProps,
    searchKeyword,
    optimized,
    getRowId,
  } = props
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      getRowId,
      initialState: {
        globalFilter: searchKeyword,
        hiddenColumns: [
          'tfNailQty',
          'tfNailType',
          'hNailQty',
          'hNailType',
          'jNailType',
          'jNailQty',
          'updatedSpec',
          'updatedWebStiffeners',
          'iciLabel',
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    useRowSelect,
  )

  const renderedRows = optimized ? rows.slice(0, 10) : rows

  return (
    <div>
      <div className="tableInfoBar printView">
        <span>{searchKeyword && `Search: ${searchKeyword}`}</span>
        <span>Show Optimized Models: {optimized ? 'Yes' : 'No'}</span>
      </div>
      <div className="nonScrollable">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderProps } = headerGroup.getHeaderGroupProps()

              return (
                <tr key={key} {...restHeaderProps}>
                  {headerGroup.headers.map((column) => {
                    const { key, ...restColumnProps } = column.getHeaderProps([
                      {
                        className: column.className,
                        style: column.style,
                      },
                      getColumnProps(column),
                      getHeaderProps(column),
                    ])
                    return (
                      <th
                        key={key}
                        {...restColumnProps}
                        className={`th ${
                          column.isSorted
                            ? column.isSortedDesc
                              ? '-sortDescending'
                              : '-sortAccending'
                            : ''
                        }`}
                      >
                        {column.render('Header')}
                      </th>
                    )
                  })}
                </tr>
              )
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {renderedRows.map((row, i) => {
              prepareRow(row)
              return (
                <tr key={i} {...row.getRowProps(getRowProps(row))}>
                  {row.cells.map((cell) => {
                    const { key, ...restCellProps } = cell.getCellProps([
                      {
                        className: cell.column.className,
                        style: cell.column.style,
                      },
                      getColumnProps(cell.column),
                      getCellProps(cell),
                    ])
                    return (
                      <td key={key} {...restCellProps}>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>

        {RenderIf(
          !rows.length,
          <NoticeMessage
            level="warning"
            title="No Matching Records Found"
            description="Try editing or deleting your search criteria."
          />,
        )}
      </div>
    </div>
  )
}

ResultTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  optimized: PropTypes.bool.isRequired,
  searchKeyword: PropTypes.string.isRequired,
  sortBy: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  getRowId: PropTypes.func.isRequired,
  getHeaderProps: PropTypes.func,
  getColumnProps: PropTypes.func,
  getRowProps: PropTypes.func,
  getCellProps: PropTypes.func,
}

ResultTable.defaultProps = {
  loading: false,
  sortBy: [],
  getHeaderProps: () => ({}),
  getColumnProps: () => ({}),
  getRowProps: () => ({}),
  getCellProps: () => ({}),
}

export default memo(ResultTable)
